<template>
  <div class="content">
    <h2 class="font-light">Questions</h2>
    <server-error v-bind:error="error.serverError"></server-error>
    <div class="row u-vertical-center">
      <search-field
        v-bind:search="filters.search"
        @handleSearch="handleSearch"
      ></search-field>
      <div class="col-4">
        <div class="form-group u-pull-right">
          <filter-dropdown
            v-bind:options="filterOptions"
            v-on:handle-filter="handleFilter"
          ></filter-dropdown>
          <router-link :to="{ name: 'question-create' }">
            <button id="addButton" class="btn-info action-button">
              <i
                class="fa-wrapper fa fa-plus-circle normal-icon"
                aria-hidden="true"
              ></i>
              New
            </button>
          </router-link>
        </div>
      </div>
    </div>
    <filter-tags v-bind:filterOptions="filterOptions"></filter-tags>
    <question-table
      v-bind:questionList="questionList"
      @getQuestionHistory="getQuestionHistory"
    ></question-table>
    <div class="row">
      <pagination-links
        v-if="pages.questionList"
        v-bind:pages="pages.questionList"
        @handlePage="handlePage"
      ></pagination-links>
      <page-size-selector
        v-on:handle-page-size="handlePageSize()"
      ></page-size-selector>
    </div>
  </div>
</template>
<style></style>
<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import { debounce } from "@/functions/debounce.function";
import { SetupFilters } from "@/services/filters.service";
import { GetSelectedFilters } from "@/services/filters.service";
import { HandleAllFilters } from "@/services/filters.service";
import { QueryHasChanged } from "@/services/filters.service";
import QuestionTable from "@/components/questions/QuestionTable";
import SearchField from "@/components/SearchField";
import FilterDropdown from "@/components/FilterDropdown";
import FilterTags from "@/components/FilterTags";
import PaginationLinks from "@/components/Pagination";
import PageSizeSelector from "@/components/PageSizeSelector";
import ServerError from "@/components/ServerError";

export default {
  name: "question-index",
  components: {
    QuestionTable,
    SearchField,
    FilterDropdown,
    FilterTags,
    PaginationLinks,
    PageSizeSelector,
    ServerError
  },
  data: function() {
    return {
      filterOptions: {
        topic: this.topics,
        is_active: [
          { name: "True", value: "true", selected: false },
          { name: "False", value: "false", selected: false }
        ],
        is_mandatory: [
          { name: "True", value: "true", selected: false },
          { name: "False", value: "false", selected: false }
        ],
        is_in_exam: [
          { name: "True", value: "true", selected: false },
          { name: "False", value: "false", selected: false }
        ],
        is_multichoice: [
          { name: "True", value: "true", selected: false },
          { name: "False", value: "false", selected: false }
        ]
      }
    };
  },
  computed: {
    ...mapGetters("questions", [
      "search",
      "filters",
      "questionList",
      "error",
      "pages",
      "topics"
    ])
  },
  watch: {
    search: debounce(function(newVal) {
      // This watches the filters.search model to debounce the input =)
      this.$store.dispatch("questions/getQuestionList");
      if (newVal != this.filters.search) {
        // Stop redundant navigation
        this.$router.replace({
          name: "question-index",
          query: { ...this.filters }
        });
      }
    }, 300),
    topics: function() {
      this.setupTopicFilter();
    }
  },
  methods: {
    handleSearch(searchVal) {
      this.$store.commit("questions/filters", {
        search: searchVal,
        page: "" // Reset the page as can't know how many pages there will be!
      });
    },
    handlePage(newPage) {
      if (newPage == 1) {
        newPage = "";
      }
      this.$store.commit("questions/filters", { page: newPage });
      this.$store.dispatch("questions/getQuestionList");
      if (QueryHasChanged(this.$route.query, this.filters)) {
        this.$router.replace({
          name: "question-index",
          query: { ...this.filters }
        });
      }
    },
    handlePageSize() {
      // Refresh the array by going to page 1 when the pageSize is changed
      this.handlePage(1);
    },
    handleFilter(payload) {
      HandleAllFilters(this.filters, this.filterOptions, payload);
      // Reset the page to 0 as we don't know how many pages there will be!
      payload.page = "";
      // Update the filters, get the new list and update the URL =)
      this.$store.commit("questions/filters", payload);
      this.$store.dispatch("questions/getQuestionList");
      this.$router.replace({
        name: "question-index",
        query: { ...this.filters }
      });
    },
    getQuestionHistory(uuid) {
      // This handler updates the page to show the history of Question.
      //
      // It clears out *all* other filter and search values and then searches
      // for the Question based on the UUID.
      for (let category in this.filterOptions) {
        // Deselect the filter control (i.e. NOT selected)
        for (let item of this.filterOptions[category]) {
          Vue.set(item, "selected", false);
        }
        // Clear the filter value in the store
        let data = {};
        data[category] = "";
        this.$store.commit("questions/filters", data);
      }
      // Set the search value.
      //
      // This will call a page refresh as 'search' is watched, so e're done =)
      this.$store.commit("questions/filters", { search: uuid });
    },
    setupTopicFilter() {
      // This function sets up the topics in the filterOptions Object whenever
      // the topics received from the server change.
      var stack = [];
      for (let topic of this.topics) {
        var entry = {};
        entry["name"] = topic.name;
        entry["value"] = topic.id;
        entry["selected"] = false;
        stack.push(entry);
      }
      this.filterOptions["topic"] = stack;
    }
  },
  mounted() {
    // Get the list of active topics and setup the Topic filter options
    if (this.topics.length == 0) {
      this.$store.dispatch("questions/getTopics");
    } else {
      this.setupTopicFilter();
    }
    // Re-hydrate any filters with either the URL query params *or* (else) in
    // with values saved in the Vuex store
    SetupFilters(this, "questions/filters", "question-index");
    // Initialize the selected filters based on the URL query params =)
    for (let key in this.$route.query) {
      GetSelectedFilters(this.filterOptions[key], this.$route.query[key]);
    }
    // If still *NO* filters are selected then select the 'is_active' filter by
    // default:
    if (Object.keys(this.filters).length == 0) {
      this.handleFilter({ is_active: "true" });
    }
    this.$store.dispatch("questions/getQuestionList");
  }
};
</script>
